// Per-tool "how it works" guide/marketing pages, served at /guides/<slug>.
// Each entry powers one SEO landing page (see src/pages/Guide.js) and links
// straight to the live tool. Keep `toolPath` in sync with the routes in App.js.

export const GUIDES = [
  {
    slug: 'lpr-pattern-analyzer',
    title: 'LPR Pattern Analyzer',
    toolPath: '/lprpattern',
    category: 'License Plate (LPR)',
    tagline: "Turn one vehicle's license-plate reads into a movement pattern.",
    description:
      "How the LPR Pattern Analyzer turns OpenALPR/REKOR CSV exports into movement patterns, hourly and day-of-week trends, common routes, and a next-camera prediction — 100% in your browser.",
    intro:
      "The LPR Pattern Analyzer ingests license-plate-reader exports for a single vehicle and reveals when and where it travels, so you can anticipate where it will show up next.",
    steps: [
      { title: 'Upload the reads', desc: 'Drop in a group-results CSV or XLSX from REKOR/OpenALPR, NYPD, or Motorola Vigilant. Add several files for the same plate and they merge into one analysis.' },
      { title: 'Automatic pattern analysis', desc: 'The tool locks onto the most-frequent plate and charts hourly activity, day-of-week trends, camera frequency, and a full timeline of reads.' },
      { title: 'Predict the next hit', desc: 'Common Routes segments the reads into trips by a time-gap threshold you control, then predicts the most likely next camera for the vehicle.' },
    ],
    features: ['Hourly & day-of-week activity patterns', 'Trip segmentation by time-gap threshold', 'Next-camera prediction', 'Map replay of every read'],
  },
  {
    slug: 'rekor-alpr-tracker',
    title: 'REKOR / ALPR Tracker',
    toolPath: '/lprtracker',
    category: 'License Plate (LPR)',
    tagline: 'Find the plates hitting the most locations across your LPR network.',
    description:
      'How the REKOR / ALPR Tracker finds license plates that appear across multiple LPR sites from REKOR/OpenALPR CSV exports, ranked by location count — entirely in your browser.',
    intro:
      'The REKOR / ALPR Tracker compares reads from many cameras at once to surface the vehicles moving across the most locations — useful for spotting a plate touring a whole area.',
    steps: [
      { title: 'Upload multiple site exports', desc: 'Add the group-results CSV exports from each LPR camera or site you want to compare.' },
      { title: 'Cross-site matching', desc: 'The tool groups every read by plate and counts how many distinct sites each plate was seen at.' },
      { title: 'Rank by spread', desc: 'Plates are ranked from most to fewest locations, so the vehicles covering the widest area rise to the top.' },
    ],
    features: ['Multi-camera cross-referencing', 'Ranked by number of distinct sites', 'Works with REKOR/OpenALPR CSV exports', 'No upload — runs locally'],
  },
  {
    slug: 'dvr-time-converter',
    title: 'DVR Time Converter',
    toolPath: '/dvrtime',
    category: 'Time & Video',
    tagline: 'Fix an unsynced DVR clock in seconds.',
    description:
      'How the DVR Time Converter reconciles a surveillance DVR clock that drifted from real time, so you can pinpoint the true time of an incident on the footage.',
    intro:
      "When a DVR's clock isn't set to real time, the timestamp burned into the video is wrong. This tool converts between real time and DVR time so you know exactly where to look.",
    steps: [
      { title: 'Anchor the clock', desc: 'Enter a known real time and the matching time shown on the DVR to establish the offset.' },
      { title: 'Convert either direction', desc: 'Enter an incident time to get the DVR time to scrub to — or enter a DVR timestamp to get the real time it happened.' },
      { title: 'Read the result', desc: 'The converted time is shown instantly, accounting for the drift between the two clocks.' },
    ],
    features: ['Two-way real-time ↔ DVR-time conversion', 'Handles any clock offset', 'No math or spreadsheets', 'Instant, offline'],
  },
  {
    slug: 'utc-converter',
    title: 'UTC Converter',
    toolPath: '/utcconverter',
    category: 'Time & Video',
    tagline: 'Convert UTC timestamps to local time — and back.',
    description:
      'How the UTC Converter quickly translates between UTC (as returned in provider records and logs) and your local time zone.',
    intro:
      'Provider returns and server logs almost always report times in UTC. This tool converts them to local time — and back — so your timeline lines up with the real world.',
    steps: [
      { title: 'Pick a direction', desc: 'Choose whether you are converting from UTC to local time or from local time to UTC.' },
      { title: 'Enter the time', desc: 'Type or select the timestamp you need to convert.' },
      { title: 'Get local context', desc: 'The converted time is displayed immediately in the target zone.' },
    ],
    features: ['UTC ↔ local conversion', 'Great for provider return timestamps', 'Instant results', 'Runs entirely offline'],
  },
  {
    slug: 'facebook-warrant-return',
    title: 'Facebook Warrant Processor',
    toolPath: '/fbscan',
    category: 'Legal Process Returns',
    tagline: 'Make a Facebook warrant return actually readable.',
    description:
      'How the Facebook Warrant Processor parses a Facebook search-warrant return into browsable account info, messages, IP addresses, and friends — no upload, all in your browser.',
    intro:
      'Facebook returns arrive as sprawling HTML that is painful to read. This tool parses it into organized, searchable sections so you can find what matters fast.',
    steps: [
      { title: 'Open the return', desc: 'Select the HTML warrant-return file. It is parsed on your device — nothing is uploaded.' },
      { title: 'Browse by section', desc: 'Account info, message threads, IP addresses, and friends are separated into clean, navigable views.' },
      { title: 'Search and follow leads', desc: 'Scan message threads and pull every IP address to build out your investigation.' },
    ],
    features: ['Structured account & subscriber info', 'Readable message threads', 'IP address extraction', 'Friends list'],
  },
  {
    slug: 'instagram-warrant-return',
    title: 'Instagram Warrant Processor',
    toolPath: '/instagramscan',
    category: 'Legal Process Returns',
    tagline: 'Turn an Instagram warrant return into an organized case file.',
    description:
      'How the Instagram Warrant Processor parses an Instagram search-warrant return into account info, photos, videos, followers, messages, and IP addresses — private and browser-based.',
    intro:
      'Instagram returns bury the useful details in raw HTML and media folders. This tool organizes everything into browsable sections you can actually work with.',
    steps: [
      { title: 'Load the return', desc: 'Open the Instagram warrant-return HTML file; optionally point the tool at the media folder to match photos and videos.' },
      { title: 'Auto-organized sections', desc: 'Account info, media, followers, direct messages, and IP history are separated and labeled.' },
      { title: 'Filter and trace', desc: 'Search conversations, review IP activity, and connect media to the account.' },
    ],
    features: ['Account & profile details', 'Photos & videos matched to the account', 'Followers and direct messages', 'IP address history'],
  },
  {
    slug: 'snapchat-subpoena-return',
    title: 'Snapchat Subpoena Processor',
    toolPath: '/snapchat',
    category: 'Legal Process Returns',
    tagline: 'Read a Snapchat subpoena return without the ZIP wrangling.',
    description:
      'How the Snapchat Subpoena Processor parses a Snapchat law-enforcement return ZIP into account details, IP history, and privacy settings — decoded locally in your browser.',
    intro:
      'Snapchat subpoena returns come as a ZIP of files. This tool reads the ZIP directly and surfaces the account details, login history, and settings in one place.',
    steps: [
      { title: 'Drop in the ZIP', desc: 'Select the Snapchat return ZIP — it is unpacked and parsed entirely on your device.' },
      { title: 'See the account at a glance', desc: 'Account details, IP/login history, and privacy settings are pulled into clean sections.' },
      { title: 'Work the timeline', desc: 'Review IP history to place the account and its activity.' },
    ],
    features: ['Reads the return ZIP directly', 'Account details & settings', 'IP and login history', 'No upload, fully offline'],
  },
  {
    slug: 'snapchat-warrant-return',
    title: 'Snapchat Warrant Processor',
    toolPath: '/snapchatwarrant',
    category: 'Legal Process Returns',
    tagline: 'Browse a Snapchat warrant return — messages, IPs, memories, and more.',
    description:
      'How the Snapchat Warrant Processor parses Snapchat search-warrant return CSV files into messages, IP history, friends, and memories — all in your browser.',
    intro:
      'A Snapchat warrant return is a stack of CSV files. This tool parses them into a single, searchable interface covering conversations, contacts, and media.',
    steps: [
      { title: 'Add the CSV files', desc: 'Select the CSVs from the return; optionally add the media directory to attach memories.' },
      { title: 'Unified case view', desc: 'Messages, friends, IP history, and memories are organized into browsable sections.' },
      { title: 'Search everything', desc: 'Filter conversations and IP activity to zero in on the relevant records.' },
    ],
    features: ['All conversations in one view', 'Friends & contact lists', 'IP history', 'Memories matched from media'],
  },
  {
    slug: 'apple-legal-process-download',
    title: 'Apple Legal Process Download',
    toolPath: '/applelegaldownload',
    category: 'Legal Process Returns',
    tagline: 'Download, decrypt, and organize an Apple return automatically.',
    description:
      'How the Apple Legal Process Download tool takes the Account_Data_Links ZIP and passphrase from Apple, then downloads, decrypts (GPG), and organizes every file by category — locally.',
    intro:
      'Apple delivers returns as encrypted links plus a passphrase. This tool automates the download-and-decrypt slog and sorts the results into categories.',
    steps: [
      { title: 'Provide the links & passphrase', desc: 'Upload the Account_Data_Links ZIP from Apple and enter the decryption passphrase.' },
      { title: 'Automatic download & decrypt', desc: 'Files are fetched and GPG-decrypted right in your browser using the passphrase.' },
      { title: 'Organized output', desc: 'Everything is grouped by category and ready to save as a clean, decrypted package.' },
    ],
    features: ['Handles the Account_Data_Links ZIP', 'In-browser GPG decryption', 'Automatic categorization', 'Passphrase never leaves your device'],
  },
  {
    slug: 'apple-legal-process-organizer',
    title: 'Apple Legal Process Organizer',
    toolPath: '/applelegal',
    category: 'Legal Process Returns',
    tagline: 'Group a messy Apple return by account for easy download.',
    description:
      'How the Apple Legal Process Organizer groups and sorts Apple legal-process returns by account so each account’s files are easy to review and download.',
    intro:
      'When an Apple return covers multiple accounts, the files are hard to separate. This tool regroups everything by account so each is easy to pull.',
    steps: [
      { title: 'Load the return', desc: 'Add the Apple legal-process return files; parsing happens on your device.' },
      { title: 'Group by account', desc: 'Files are automatically clustered by the account they belong to.' },
      { title: 'Download per account', desc: 'Grab a clean, organized package for each account.' },
    ],
    features: ['Automatic grouping by account', 'Clean per-account downloads', 'Handles multi-account returns', 'Runs entirely offline'],
  },
  {
    slug: 'apple-2703d-parser',
    title: 'Apple Subpoena & 2703(d) Parser',
    toolPath: '/apple2703d',
    category: 'Legal Process Returns',
    tagline: 'Read an Apple subpoena or 2703(d) return without opening every spreadsheet.',
    description:
      'How the Apple Subpoena & 2703(d) Parser turns an Apple subpoena or court-order return into one readable view: subscriber details, iCloud activity, iMessage and FaceTime queries, mail logs, devices and IP addresses, with analysis and a PDF case summary.',
    intro:
      'An Apple subpoena or 2703(d) return is a stack of spreadsheets and notes for each account. The parser reads them all, explains what each sheet means, and lines the records up into one timeline you can search and filter.',
    steps: [
      { title: 'Load the return', desc: 'Add the ZIP from the Apple Legal Process Download tool, Apple’s original production ZIP, or loose .xlsx and .csv files. Everything is read on your device.' },
      { title: 'Browse every record', desc: 'Subscriber details, iCloud service logs, iMessage and FaceTime capability queries, FaceTime invitations, iCloud Mail logs and devices, each with a plain-language note on what it does and doesn’t prove.' },
      { title: 'Analyze and report', desc: 'See pattern of life, activity in an incident window, likely home and work networks, links between accounts and security events, then download a PDF case summary.' },
    ],
    features: [
      'Merged timeline across every sheet',
      'IP addresses with days, nights and workdays seen',
      'VPN, Tor and hosting screening done locally',
      'Multi-account returns with account matching',
      'PDF case summary',
      'Runs entirely in your browser',
    ],
  },
  {
    slug: 'preservation-request',
    title: 'Preservation Tool',
    toolPath: '/preservation',
    category: 'Case Tools',
    tagline: 'Generate a preservation request in a minute.',
    description:
      'How the Preservation Tool builds ready-to-send preservation requests for phone records or area searches, output as a formatted document.',
    intro:
      'Preservation letters are routine but tedious. This tool assembles a properly formatted request for phone records or an area search from a few inputs.',
    steps: [
      { title: 'Pick the request type', desc: 'Choose a phone-records or area-search preservation request.' },
      { title: 'Fill in the specifics', desc: 'Add the identifiers, dates, and provider details for the request.' },
      { title: 'Generate the document', desc: 'Download a formatted preservation request ready to send.' },
    ],
    features: ['Phone-record & area-search templates', 'Provider-aware formatting', 'One-click document generation', 'No case data leaves your browser'],
  },
  {
    slug: 'subpoena-rider',
    title: 'Provider Directory & Rider Generator',
    toolPath: '/subpoenarider',
    category: 'Case Tools',
    tagline: 'Find who to serve, then build the rider or warrant attachment.',
    description:
      'A directory of 196 providers (social media, AI chatbots, carriers, ISPs, hosting, payment apps, crypto and Bitcoin ATMs, banks and more) with how to serve each, emergency/exigent contacts, and what a subpoena, 2703(d) order or warrant can reach, plus a Word rider generator.',
    intro:
      'Paste an email, phone number, profile URL, username or IP address and the tool tells you who holds the records, how they take legal process and emergency requests, and what each level of process can get. Then it builds the rider for you.',
    steps: [
      { title: 'Paste what you have', desc: 'An email, phone, URL, handle, $cashtag, IP, IMEI or wallet address, or just search a company name.' },
      { title: 'Check the provider', desc: 'See the portal, law enforcement guide, emergency process, user-notice policy, and which records need a subpoena, 2703(d) order or warrant.' },
      { title: 'Build the rider', desc: 'Pick the legal process; the records it can reach are pre-checked. Add identifiers and dates, then download a Word rider or warrant attachment.' },
    ],
    features: ['196-provider directory', 'Emergency / exigent contacts', 'Subpoena, 2703(d) and warrant record lists', 'Word (.docx) output, built locally'],
  },
  {
    slug: 'common-analysis',
    title: 'Common Analysis',
    toolPath: '/commonanalysis',
    category: 'Case Tools',
    tagline: 'Find the items common to multiple datasets.',
    description:
      'How Common Analysis finds items shared across multiple datasets — such as LPR reads or phone numbers — from a simple spreadsheet.',
    intro:
      'Looking for the number, plate, or device that shows up across several lists? Common Analysis compares columns and surfaces the overlaps.',
    steps: [
      { title: 'Prep your spreadsheet', desc: 'Download the template and paste each dataset you want to compare into its own column.' },
      { title: 'Upload the file', desc: 'Add the .xlsx file; it is processed on your device.' },
      { title: 'See the overlaps', desc: 'The tool reports the items that appear across the datasets and exports the results.' },
    ],
    features: ['Compare many datasets at once', 'Works with phone, LPR, or any list', 'Template included', 'Excel output'],
  },
  {
    slug: 'exif-viewer',
    title: 'EXIF Viewer',
    toolPath: '/exif',
    category: 'Case Tools',
    tagline: 'Read the hidden metadata inside a photo.',
    description:
      'How the EXIF Viewer extracts GPS coordinates, device info, and timestamps from photos (JPEG, HEIC, PNG, and more) without uploading them anywhere.',
    intro:
      'Photos carry hidden metadata: where and when they were taken and on what device. The EXIF Viewer surfaces it — and never uploads your image.',
    steps: [
      { title: 'Drop in a photo', desc: 'Select or drag in an image (JPEG, HEIC, PNG, and more). It stays on your device.' },
      { title: 'Read the metadata', desc: 'GPS coordinates, camera/device info, and timestamps are extracted instantly.' },
      { title: 'Map the location', desc: 'Jump from embedded GPS coordinates straight to a map to place the photo.' },
    ],
    features: ['GPS coordinates with map link', 'Camera & device details', 'Original timestamps', 'Supports JPEG, HEIC, PNG, and more'],
  },
  {
    slug: 'cdr-analyzer',
    title: 'CDR Analyzer',
    toolPath: '/cdranalyzer',
    category: 'Case Tools',
    tagline: 'Make sense of a carrier call-detail record.',
    description:
      'How the CDR Analyzer parses carrier call-detail records (CDRs) into a readable call and contact timeline, identifying the subscriber and top contacts.',
    intro:
      'Carrier CDRs are dense and vary by provider. This tool detects the format, identifies the subscriber, and organizes calls and contacts for analysis.',
    steps: [
      { title: 'Upload the CDR', desc: 'Add the carrier CSV or XLSX call-detail record; it is parsed locally.' },
      { title: 'Auto-detect & identify', desc: 'The tool recognizes the carrier format and identifies the subscriber number.' },
      { title: 'Analyze contacts', desc: 'Review calls by contact, direction, and time to find patterns and top associates.' },
    ],
    features: ['Multi-carrier format detection', 'Subscriber identification', 'Top-contact analysis', 'Searchable call timeline'],
  },
  {
    slug: 'map-search',
    title: 'Map Search',
    toolPath: '/mapsearch',
    category: 'Case Tools',
    tagline: 'Turn visible map features into location leads.',
    description:
      'How Map Search finds locations where OpenStreetMap features occur near each other — from addressed buildings and building levels to cameras, transit, and critical infrastructure.',
    intro:
      'When an image or lead gives you a few recognizable features but not a location, Map Search searches OpenStreetMap for places where those features occur together. It is a streamlined way to turn clues into map leads.',
    steps: [
      { title: 'Choose the clues', desc: 'Select investigative presets such as house numbers, building stories, entrances, cameras, fuel stations, transit, or public-safety infrastructure. Add an exact OpenStreetMap tag when you need something more specific.' },
      { title: 'Draw the area', desc: 'Search within a rectangle, radius circle, or free-form polygon. Smaller, well-targeted areas return more quickly and produce stronger leads.' },
      { title: 'Review and export leads', desc: 'Map Search returns locations where every selected feature appears within your chosen distance. Open a candidate in OpenStreetMap or Google Maps, or export the results as CSV or KML.' },
    ],
    features: ['Nearby-feature matching', 'Building, address-number, level-count & entrance presets', 'Rectangle, circle & free-form polygon search areas', 'Custom OSM-tag searches', 'CSV & KML export'],
    privacyNote: <><strong>Online map lookup.</strong> Map Search loads OpenStreetMap tiles and sends the selected search area and feature tags to OpenStreetMap&rsquo;s Nominatim and Overpass services. LE Tech Tools does not store those searches.</>,
  },
  {
    slug: 'ping-mapper',
    title: 'Ping Mapper',
    toolPath: '/pingmapper',
    category: 'Case Tools',
    tagline: 'Turn a cell-ping coordinate and radius into a map you can actually read.',
    description:
      'How Ping Mapper turns a cell-phone ping’s GPS coordinate and radius from a carrier return into a shaded search-area circle on a map.',
    intro:
      'A cell-ping return gives you a coordinate and a radius in meters — numbers that are hard to picture on their own. Ping Mapper plots them directly: paste the coordinate, set the radius, and see the actual search area on a map.',
    steps: [
      { title: 'Paste the coordinate', desc: 'Drop in the latitude and longitude from the ping report — comma or space separated, with or without N/S/E/W letters.' },
      { title: 'Set the radius', desc: 'Enter the radius in meters reported alongside the ping.' },
      { title: 'Read the map', desc: 'A marker and a shaded circle show the reported search area to scale, auto-centered and zoomed to fit — with one-click links out to Google Maps and OpenStreetMap.' },
    ],
    features: ['Tolerant coordinate parsing (comma/space separated, N/S/E/W letters, degree symbols)', 'Radius-in-meters circle overlay, to scale', 'Auto-fit map view', 'One-click open in Google Maps / OpenStreetMap'],
    privacyNote: <><strong>Loads map tiles only — nothing you enter is sent anywhere.</strong> Ping Mapper fetches OpenStreetMap map tiles to display the area around the coordinate you paste. The coordinate and radius themselves are never transmitted as a search or query — they stay in your browser, like everything else in this tool.</>,
  },
  {
    slug: 'ip-domain-intelligence',
    title: 'IP and Domain Intelligence',
    toolPath: '/iplookup',
    category: 'Case Tools',
    tagline: 'Screen IPs against VPN, Tor, and abuse lists offline — send anything out only when you choose to.',
    description:
      'How the IP and Domain Intelligence tool screens IP addresses against VPN, datacenter, Tor, and abuse blocklists entirely offline, then offers opt-in live registration, geolocation, and hostname lookups — plus domain and email mail-provider identification.',
    intro:
      'A return or log full of IP addresses raises the same questions every time: is this a VPN, is it worth a subpoena, who do you even serve? This tool answers the free, offline questions first — VPN, datacenter, Tor, and abuse-list matches — then lets you opt in, address by address, to the live lookups that leave your device.',
    steps: [
      { title: 'Paste addresses or a whole log', desc: 'Drop in IPs one per line, comma-separated, or an entire log file — they are extracted and de-duplicated automatically. Screening against VPN, datacenter, Tor-exit, iCloud Private Relay, and abuse blocklists cached in your browser runs entirely offline; nothing you paste is transmitted.' },
      { title: 'Add live lookup only when you want it', desc: 'Opt in per address (or in bulk, with a confirmation past a threshold) to send just that IP to RDAP, three geolocation providers, reverse DNS, BGP origin, and Shodan for registration, ownership, hostname, and open-port data.' },
      { title: 'Or switch to domains & email', desc: 'Paste a domain, URL, or email address — only the domain is ever sent — to see who actually handles its mail (who to serve legal process on), registrar and nameserver info, and SPF/DMARC records.' },
    ],
    features: ['Offline VPN, Tor, datacenter, iCloud Private Relay & abuse-list screening', 'Opt-in live RDAP, geolocation, PTR & Shodan lookup', 'Domain & email mail-provider identification (who to serve)', 'Built-in "check my own connection" VPN verifier', 'CSV export for both IP and domain results'],
    // The one tool that can leave the browser for something other than the two
    // Firestore exceptions — every send is opt-in and named in the UI, so the
    // guide's privacy callout gets its own honest wording (see `network` below).
    network: true,
  },
  {
    slug: 'commercial-phone-lookup',
    title: 'Commercial Phone Number Lookup',
    toolPath: '/phonelookup',
    category: 'Case Tools',
    tagline: 'Bulk-check phone numbers against ~15M US businesses — 100% locally.',
    description:
      'How the Commercial Phone Number Lookup tool bulk-checks phone numbers against a ~15M-record US business dataset (Foursquare Open Source Places) built into the site, entirely in your browser.',
    intro:
      'A number turns up in a return or a canvass and you need to know: is this a business, and which one? This tool checks a pasted list of numbers against a ~15M-record US business dataset built right into the site to surface a name and address in seconds — no number you paste is ever sent anywhere. It only identifies businesses, never personal callers.',
    steps: [
      { title: 'Paste your numbers', desc: 'Drop in numbers one per line, comma-separated, or pulled out of an entire report — they\'re extracted and de-duplicated automatically, in any common formatting.' },
      { title: 'Matched instantly and privately', desc: 'Each number is checked against a ~15M-record US business dataset (Foursquare Open Source Places) built into this site. Only the data file for that number\'s area code is fetched, from this site itself — the number is never sent anywhere.' },
      { title: 'Review and export', desc: 'Expand a match to see the business name, address, and category, or export every result to CSV.' },
    ],
    features: ['~15M US business records (Foursquare Open Source Places)', 'Bulk paste with automatic number extraction & de-duplication', '100% local — no number you paste is ever transmitted', 'CSV export of every match', 'No API key or account required'],
  },
  {
    slug: 'video-canvass',
    title: 'Video Canvass Tracker',
    toolPath: '/canvass',
    category: 'Field Operations',
    tagline: 'Run a live, shared video canvass with your whole team.',
    description:
      'How the Video Canvass Tracker gives a team pulling surveillance video one shared live map to log each address, mark footage results, and track a subject in real time.',
    intro:
      'When a team fans out to pull surveillance video, coordination is everything. The Video Canvass Tracker puts every address, result, and direction on one shared map that updates live.',
    steps: [
      { title: 'Create a canvass', desc: 'Start a canvass and share the private link with your team — no accounts needed.' },
      { title: 'Log each address', desc: 'Drop a pin per location and mark whether footage was obtained and if the subject was seen (and which way they went).' },
      { title: 'Track progress live', desc: 'Everyone sees updates in real time, so the team always knows what has been covered.' },
    ],
    features: ['Real-time shared map', 'Footage-obtained & subject-seen status', 'Direction-of-travel arrows', 'Private capability-link access'],
    // One of two tools that syncs to the cloud (by design) — note it in the guide.
    cloud: true,
  },
  {
    slug: 'area-search-coordinator',
    title: 'Area Search Coordinator',
    toolPath: '/areasearch',
    category: 'Field Operations',
    tagline: 'See what ground your team has covered while searching an area — live.',
    description:
      'How the Area Search Coordinator gives a team sweeping a neighborhood for a vehicle one shared live map: mark the target zone, stream each participant\'s driven route in real time, and drop pins with notes.',
    intro:
      'When a few officers fan out to search a neighborhood for a vehicle, it is hard to know which streets have already been checked. The Area Search Coordinator marks the target zone and draws every participant\'s route live, so the team can see what has been covered and what hasn\'t.',
    steps: [
      { title: 'Mark the search zone', desc: 'Start a search and draw the target area as a radius circle or a freeform shape, then share the private link with your team — no accounts needed.' },
      { title: 'Share your location', desc: 'Each participant opts in to share their live GPS position; their driven route draws on the shared map in real time as they move.' },
      { title: 'Drop pins and track coverage', desc: 'Flag a spot with a note — checked, suspicious, or otherwise — and watch the whole team\'s coverage build live on one map.' },
    ],
    features: ['Real-time shared coverage map', 'Radius or freeform search-zone boundary', 'Opt-in live location trail per participant', 'Pins with notes and reverse-geocoded addresses', 'Private capability-link access'],
    // One of two tools that syncs to the cloud (by design) — note it in the guide.
    cloud: true,
  },
  {
    slug: 'case-timeline-builder',
    title: 'Case Timeline Builder',
    toolPath: '/timeline',
    category: 'Case Tools',
    tagline: 'Turn scattered evidence into one chronological, presentation-ready timeline.',
    description:
      'How the Case Timeline Builder assembles phone records, screenshots, interviews, and reports into a single chronological timeline with a map of where each event happened, exported as a polished PDF — saved locally on your device.',
    intro:
      'A case comes from everywhere — call logs, surveillance screenshots, interview notes, police reports. The Case Timeline Builder gives you one place to line it all up by date, tag it with where it happened, and turn it into a report you can hand to someone else.',
    steps: [
      { title: 'Add events as you find them', desc: 'Each event gets a date (exact, approximate, or date-only), a category, notes, and optionally a photo, a map location, and a reference file like the actual report.' },
      { title: 'See it as a timeline or a map', desc: 'Browse chronologically, filter by category or search, or switch to a map view showing every geolocated event at once.' },
      { title: 'Export a polished PDF', desc: 'Generate a cover page, an overview map with a category legend, and a card for every event — photo and location side by side — ready to share or present.' },
    ],
    features: ['Exact, approximate, or date-only timestamps', 'Photo attachments with GPS/date auto-suggestion', 'Reference-file attachments (reports, documents)', 'Case overview map, color-coded by category', 'Polished PDF export', 'Saved locally with JSON export/import for backup'],
  },
  {
    slug: 'audio-video-transcription',
    title: 'Audio & Video Transcription',
    toolPath: '/transcribe',
    category: 'Time & Video',
    tagline: 'Transcribe interviews and jail calls with Whisper — no upload, ever.',
    description:
      'How the Audio & Video Transcription tool turns interviews, jail calls, and body-cam audio into timestamped, searchable text using OpenAI’s Whisper — running entirely in your browser, with optional speaker identification and an AI summary.',
    intro:
      'Interviews, jail calls, body-cam audio, voicemails, 911 recordings — this tool transcribes them locally, using the Whisper speech-recognition model downloaded once and cached in your browser. Nothing about the recording is ever uploaded: after the model loads, you can disconnect from the internet and keep transcribing.',
    steps: [
      { title: 'Drop in one file or a batch', desc: 'MP3, WAV (including legacy µ-law, A-law and ADPCM codecs common on jail-call and DVR audio), M4A, FLAC, OGG, and the audio track of MP4/MOV. Files process one after another; the model loads once for the whole batch.' },
      { title: 'Optionally identify speakers and summarize', desc: 'Turn on speaker identification to label who said what (with click-to-correct if it gets a segment wrong), and an AI summary to get the gist of a long recording — both run locally too, and both are clearly labeled: the transcript is a machine transcription, and the summary is AI-written and can state things that were never actually said.' },
      { title: 'Export everything', desc: 'Per-file transcript, timestamped text, and speaker-labeled text, or a ZIP with every version for the whole batch plus one combined Excel workbook — one row per recording, every version in its own column.' },
    ],
    features: ['Runs Whisper entirely in-browser, no upload', 'Handles legacy WAV codecs (µ-law, A-law, ADPCM)', 'Batch processing, one model load per batch', 'Opt-in speaker identification with manual correction', 'Opt-in local AI summary, clearly labeled as non-verbatim', 'Excel workbook + ZIP export of every version'],
  },
  {
    slug: 'audio-format-converter',
    title: 'Audio Format Converter',
    toolPath: '/audioconverter',
    category: 'Time & Video',
    tagline: 'Turn a jail-call codec no player can open into an ordinary MP3 or WAV.',
    description:
      'How the Audio Format Converter decodes audio saved in an unusual or legacy codec — the kind that needs a special player or codec pack — and writes out an ordinary MP3 or WAV file that opens anywhere, entirely in your browser.',
    intro:
      'Jail-call recorders, DVRs, and old voice recorders often save audio in a codec (µ-law, A-law, ADPCM, and similar) that Windows Media Player, QuickTime, or a courtroom AV system can’t open without extra software. This tool decodes that audio and writes out a standard MP3 or WAV file that plays anywhere, with nothing extra to install.',
    steps: [
      { title: 'Drop in one or many files', desc: 'The tool reads the container directly — including the legacy codecs a browser normally rejects — rather than trusting the file extension.' },
      { title: 'Choose MP3 or WAV', desc: 'WAV keeps every channel and the exact original sample rate with zero quality loss. MP3 is smaller and universally playable, at a bitrate you choose.' },
      { title: 'Convert and download', desc: 'Each file converts in the background so the page stays responsive even on a long recording. Download individually or as one ZIP for the whole batch.' },
    ],
    features: ['Decodes legacy WAV codecs (µ-law, A-law, ADPCM)', 'MP3 or lossless WAV output', 'Batch conversion with background encoding', 'No upload — zero network requests of any kind', 'ZIP export of a whole batch'],
  },
  {
    slug: 'file-hash-verifier',
    title: 'File Hash / Chain-of-Custody Verifier',
    toolPath: '/hashverifier',
    category: 'Case Tools',
    tagline: 'Prove a file was not altered — hash it before and after it moves.',
    description:
      'How the File Hash / Chain-of-Custody Verifier computes SHA-256, SHA-1, MD5, and SHA-512 hashes of an evidence file and checks them against a known value, entirely in your browser, with a hash log or PDF report you can attach to a case file.',
    intro:
      'A file that moves — copied to another drive, sent to a lab, attached to a report — needs proof it was not altered along the way. This tool computes cryptographic hashes locally, using the same functions built into your browser, and lets you check a file against a hash you recorded earlier.',
    steps: [
      { title: 'Drop in a file', desc: 'Any file type — disk images, evidence photos and video, documents. Add several at once for a batch.' },
      { title: 'Choose your algorithms', desc: 'SHA-256 is the modern standard and is checked by default. MD5 and SHA-1 are available for matching against hash values already on record in legacy forensic tooling.' },
      { title: 'Verify and export', desc: 'Paste a previously recorded hash to check it instantly, then export a plain-text hash log or a printable PDF report for chain-of-custody documentation.' },
    ],
    features: ['SHA-256, SHA-1, MD5, and SHA-512', 'Instant comparison against a known hash', 'Runs in a background worker — the page stays responsive on large files', 'Plain-text hash log and PDF report export', 'Zero network requests'],
  },
  {
    slug: 'image-converter',
    title: 'Image Converter',
    toolPath: '/imageconverter',
    category: 'Case Tools',
    tagline: 'Turn iPhone HEIC photos into JPG or PNG that open anywhere.',
    description:
      'How the Image Converter turns iPhone HEIC photos into ordinary JPG or PNG files entirely in your browser — nothing is uploaded — and what it does and does not preserve.',
    intro:
      'Photos from an iPhone are often saved as HEIC, which many report systems, older Windows machines, and email clients cannot open. This tool converts them locally into JPG or PNG so they can be viewed, attached, or printed anywhere, without sending the photos to a server.',
    steps: [
      { title: 'Drop in your photos', desc: 'Add one or many .heic or .heif files. JPG, PNG, and WebP files are accepted too.' },
      { title: 'Choose JPG or PNG', desc: 'JPG makes small files that open everywhere, with a quality slider (92% by default). PNG is lossless but produces much larger files.' },
      { title: 'Convert and download', desc: 'Files convert one at a time, and one bad file does not stop the rest. Download each result, or the whole batch as a ZIP.' },
      { title: 'Keep the original', desc: 'Converted copies do not keep EXIF metadata (GPS, device, timestamps) and have different hashes than the original. Keep the HEIC as your evidence copy and read its metadata with the EXIF Viewer.' },
    ],
    features: ['HEIC/HEIF to JPG or PNG', 'Adjustable JPG quality', 'Batch conversion with ZIP download', 'No upload — zero network requests', 'Original file is never modified'],
  },
  {
    slug: 'vin-decoder',
    title: 'VIN Decoder',
    toolPath: '/vindecoder',
    category: 'License Plate (LPR)',
    tagline: 'Validate a VIN’s check digit and decode it — locally, or fully via NHTSA.',
    description:
      'How the VIN Decoder validates a Vehicle Identification Number’s check digit and decodes its country of origin, likely manufacturer, and model year entirely in your browser, with an optional full decode (exact make, model, engine, and plant) via NHTSA’s free public database.',
    intro:
      'Every VIN encodes a real, verifiable check digit — catching a transcription error before it derails a records request. This tool validates that locally, decodes what the VIN standard itself defines (country, a likely manufacturer, and candidate model years), and offers an optional, clearly-disclosed lookup against NHTSA’s public database for the exact make, model, and trim.',
    steps: [
      { title: 'Enter a 17-character VIN', desc: 'Checked locally as you type — format validity, the check digit, country of origin, a likely manufacturer, and candidate model years, with no network request.' },
      { title: 'Full decode (optional)', desc: 'Press Full decode to send just the VIN to NHTSA’s free public vPIC database for the exact make, model, engine, body class, and manufacturing plant.' },
    ],
    features: ['Real check-digit validation (49 CFR 565 / ISO 3779)', 'Country of origin and likely manufacturer, decoded locally', 'Model-year decoding with the position-7 disambiguation convention', 'Optional full decode via NHTSA’s free public API — clearly disclosed, never automatic'],
  },
];

export const getGuide = slug => GUIDES.find(g => g.slug === slug);
export const guideForTool = toolPath => GUIDES.find(g => g.toolPath === toolPath);
